import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Calendar, ChevronLeft, ChevronRight, Factory, Download, Loader2, Plus,
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-400 text-white',
  released: 'bg-blue-500 text-white',
  wip: 'bg-yellow-500 text-black',
  completed: 'bg-green-500 text-white',
};

export function ProductionStatusTab({
  workOrders,
  loadingWOs,
  dateColumns,
  viewStartDate,
  daysToShow,
  formatDate,
  getWOBarStyle,
  onNavigateWeek,
  onSetViewStartDate,
  onDownloadSchedulePDF,
  onCreateWorkOrder,
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" /> Production Schedule
            </CardTitle>
            <CardDescription>Work Orders and timeline for this Sales Order</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onNavigateWeek(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium px-2">
              {new Date(viewStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - 
              {new Date(new Date(viewStartDate).setDate(new Date(viewStartDate).getDate() + daysToShow - 1)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <Button variant="outline" size="sm" onClick={() => onNavigateWeek(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onSetViewStartDate(new Date().toISOString().split('T')[0])}>
              Today
            </Button>
            <Button variant="outline" onClick={onDownloadSchedulePDF}>
              <Download className="mr-2 h-4 w-4" /> Download Schedule
            </Button>
            <Button onClick={onCreateWorkOrder} data-testid="create-wo-btn">
              <Plus className="mr-2 h-4 w-4" /> Create Work Order
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loadingWOs ? (
          <div className="text-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground mt-2">Loading production schedule...</p>
          </div>
        ) : workOrders.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Factory className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No Work Orders created for this Sales Order yet.</p>
            <p className="text-sm">Create one right here, or plan several at once from Production Planning.</p>
            <Button className="mt-4" onClick={onCreateWorkOrder} data-testid="create-wo-empty-btn">
              <Plus className="mr-2 h-4 w-4" /> Create Work Order
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              <div className="flex border-b">
                <div className="w-56 flex-shrink-0 p-2 font-medium bg-muted">Work Order</div>
                <div className="flex-1 flex">
                  {dateColumns.map((col) => (
                    <div
                      key={col.date}
                      className={`flex-1 text-center p-1 text-xs border-l ${col.isWeekend ? 'bg-gray-100' : ''} ${col.isToday ? 'bg-primary/10' : ''}`}
                    >
                      <div className="font-medium">{col.dayName}</div>
                      <div>{col.dayNum}</div>
                    </div>
                  ))}
                </div>
              </div>

              {workOrders.map((wo) => {
                const barStyle = getWOBarStyle(wo);
                return (
                  <div key={wo.id} className="flex border-b hover:bg-muted/30">
                    <div className="w-56 flex-shrink-0 p-2 border-r bg-muted/20">
                      <div className="font-mono font-medium text-sm">{wo.wo_number}</div>
                      <div className="text-xs text-muted-foreground truncate">{wo.part_internal_no}</div>
                      <div className="text-xs text-muted-foreground">{wo.qty_target?.toLocaleString()} pcs</div>
                      <Badge className={`mt-1 text-xs ${STATUS_COLORS[wo.status] || 'bg-gray-400 text-white'}`}>
                        {wo.status?.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="flex-1 relative h-20">
                      {barStyle && (
                        <div
                          className={`absolute top-4 h-12 rounded px-2 py-1 text-xs overflow-hidden shadow-sm ${STATUS_COLORS[wo.status] || 'bg-gray-400 text-white'}`}
                          style={{ left: barStyle.left, width: barStyle.width }}
                          title={`${wo.wo_number}: ${wo.part_internal_no} (${wo.qty_target} pcs)`}
                        >
                          <div className="font-medium truncate">{wo.wo_number}</div>
                          <div className="truncate">{formatDate(wo.planned_start_date)} - {formatDate(wo.planned_end_date)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {workOrders.length > 0 && (
          <div className="mt-6 border-t pt-4">
            <h4 className="font-medium mb-3">Work Orders Summary</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>WO #</TableHead>
                  <TableHead>Part</TableHead>
                  <TableHead>Machine</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workOrders.map((wo) => (
                  <TableRow key={wo.id}>
                    <TableCell className="font-mono font-medium">{wo.wo_number}</TableCell>
                    <TableCell className="font-mono text-sm">{wo.part_internal_no}</TableCell>
                    <TableCell>{wo.machine_name || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                    <TableCell className="text-right">{wo.qty_target?.toLocaleString()}</TableCell>
                    <TableCell>{formatDate(wo.planned_start_date)}</TableCell>
                    <TableCell>{formatDate(wo.planned_end_date)}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[wo.status] || 'bg-gray-400'}>{wo.status?.toUpperCase()}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
